import { Badge } from "../../../components/ui/badge";
import { Check, Clock, X } from "lucide-react";
import { DebugOverlay } from "../../../debug";
import { ConcurrentCoursePanel } from "./ConcurrentCoursePanel";
import { BandLogEntryForm } from "../../baende/components/BandLogEntryForm";
import type { ConcurrentCoursePanelProps } from "./ConcurrentCoursePanel";
import { FuturePreviewContent } from "./FuturePreviewContent";
import { useUpsertDiaryEntryMutation } from "../../klassenbuch/klassenbuch/api/mutations";

// ============================================================================
// Types
// ============================================================================

export interface OverviewSectionProps {
  overviewData: any;
  selectedLesson: any;
  recordedMeta: { name: string; atISO: string } | null;
  prefilledFromPrevious: boolean;
  prefilledAny: boolean;
  diaryMeta: { name: string; atISO: string } | null;
  formatStudentName: (name: string) => string;
  // ConcurrentCoursePanel pass-through
  courseOverlaps?: ConcurrentCoursePanelProps["courseOverlaps"];
  concurrentCourseAttendance: any;
  transferredStudentIds: Set<string>;
  lessonId?: string;
  isTransferring: boolean;
  pendingTransfer: ConcurrentCoursePanelProps["pendingTransfer"];
  setPendingTransfer: ConcurrentCoursePanelProps["setPendingTransfer"];
  transferReason: string;
  setTransferReason: (v: string) => void;
  onTransferConfirm: (studentId: string, fromLessonId: string) => Promise<void>;
  /** When true, renders a read-only future lesson preview */
  isFuturePreview?: boolean;
  /** Protection data per student (sick, checked-out, etc.) */
  protectionByStudent?: Record<string, { protected: boolean; reasons: string[]; isAbsentToday: boolean }>;
  /** Diary note text and setter for inline editing */
  lessonNote?: string;
  setLessonNote?: (v: string) => void;
  /** Whether the user can edit the diary entry */
  canEditDiary?: boolean;
  /** Students kept in main lesson (for course popup banner) */
  keptInMainLesson?: Array<{
    id: string;
    name: string;
    notes: string;
    fromLessonId: string | null;
    mode: "present" | "pending";
  }>;
  /** Refresh bundle after a Tagesgast add (only used in future-preview mode). */
  reloadBundle?: () => Promise<void>;
}

export function OverviewSection({
  overviewData,
  selectedLesson,
  recordedMeta,
  prefilledFromPrevious,
  prefilledAny,
  diaryMeta,
  formatStudentName,
  courseOverlaps,
  concurrentCourseAttendance,
  transferredStudentIds,
  lessonId,
  isTransferring,
  pendingTransfer,
  setPendingTransfer,
  transferReason,
  setTransferReason,
  onTransferConfirm,
  isFuturePreview = false,
  protectionByStudent,
  lessonNote = "",
  setLessonNote,
  canEditDiary = false,
  keptInMainLesson = [],
  reloadBundle,
}: OverviewSectionProps) {
  const [upsertDiary, { isLoading: isSavingDiary }] = useUpsertDiaryEntryMutation();

  if (!overviewData) {
    return (
      <div className="py-8 text-center text-sm text-gray-500">
        Lade Übersicht…
      </div>
    );
  }

  const isCourse = !!(selectedLesson as any)?.is_course;
  const isCourseShortNoticeCancelled = !!(selectedLesson as any)
    ?.course_short_notice_cancelled;
  const courseShortNoticeReason =
    ((selectedLesson as any)?.course_short_notice_reason as string | null) ||
    null;

  // ── Future lesson preview: student roster + diary + timing banner ────
  if (isFuturePreview) {
    const students = [
      ...(overviewData.pending || []),
      ...(overviewData.present || []),
    ].sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "", "de"));

    const getProtectionLabel = (studentId: string): string | null => {
      const p = protectionByStudent?.[studentId];
      if (!p?.protected) return null;
      // In the future-lesson preview the teacher only needs to know "we
      // know this child won't be here" — the detailed reason (krankgemeldet,
      // entschuldigt, kurzbeschulung, …) is exposed elsewhere in the
      // workflow. Surface a single "Informiert" label so the pre-lesson
      // view matches the cross-feature naming used by OverviewSection /
      // TripSummaryView for the same concept.
      if (
        p.reasons.includes("krankgemeldet") ||
        p.reasons.includes("checked_out") ||
        p.reasons.includes("daily_log.absent_excused") ||
        p.reasons.includes("daily_log.absent_unexcused") ||
        p.reasons.includes("daily_log.absent_unclear") ||
        p.reasons.includes("kurzbeschulung")
      ) {
        return "Informiert";
      }
      return null;
    };

    /** Labels that should render in grey rather than red. None today —
     *  the future preview now uses a single "Informiert" label which is
     *  treated as a real (red-tinted) informed-of-absence state. */
    const isGreyLabel = (_label: string) => false;

    return <FuturePreviewContent
      students={students}
      isCourse={isCourse}
      formatStudentName={formatStudentName}
      getProtectionLabel={getProtectionLabel}
      isGreyLabel={isGreyLabel}
      lessonNote={lessonNote}
      setLessonNote={setLessonNote}
      canEditDiary={canEditDiary}
      lessonId={lessonId}
      lessonName={(selectedLesson as any)?.subject}
      bandClassId={(selectedLesson as any)?.class_id ?? null}
      bandLessonDateStr={(selectedLesson as any)?.start_datetime ?? null}
      bandPeriodNumber={
        (selectedLesson as any)?.period_number ??
        (selectedLesson as any)?.block_number ??
        null
      }
      onSaveDiary={async () => {
        if (!lessonId) return;
        try {
          await upsertDiary({ lessonId, entryText: lessonNote, homework: null }).unwrap();
        } catch (err) {
          console.error("[FuturePreview] Diary save error:", err);
        }
      }}
      isSavingDiary={isSavingDiary}
      courseOverlaps={courseOverlaps}
      concurrentCourseAttendance={concurrentCourseAttendance}
      reloadBundle={reloadBundle}
    />;
  }

  return (
    <div className="space-y-6">
      {/* Prefill alert */}
      {!recordedMeta && (prefilledFromPrevious || prefilledAny) && (
        <div
          role="alert"
          className="rounded-md border-2 border-amber-500 bg-amber-100 text-amber-900 px-4 py-3 text-sm lg:text-base font-medium"
        >
          Anwesenheit wurde übernommen. Bitte prüfen und auf „Anwesenheit
          speichern" klicken
        </div>
      )}

      {/* Kept-in-main-lesson banner (course popups only) */}
      {keptInMainLesson.length > 0 && (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-4 py-3">
          <h4 className="text-sm font-semibold text-amber-900 mb-2">
            ⚠️ Im Klassenunterricht geblieben ({keptInMainLesson.length})
          </h4>
          <div className="space-y-1">
            {keptInMainLesson.map((s) => (
              <div key={s.id} className="text-sm text-amber-800">
                <span className="font-medium">{formatStudentName(s.name)}</span>
                {s.mode === "pending" ? (
                  <span className="ml-2 inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900">
                    Ausstehend
                  </span>
                ) : (
                  <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800">
                    ✓ Anwesend
                  </span>
                )}
                {s.notes && (
                  <span className="text-amber-600 italic ml-1">— {s.notes}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isCourse && isCourseShortNoticeCancelled && (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-amber-200 text-amber-900">
              KA
            </span>
            <h4 className="text-sm font-semibold text-amber-900">
              Kurzfristig ausgefallen
            </h4>
          </div>
          {courseShortNoticeReason && (
            <div className="text-sm text-amber-800">{courseShortNoticeReason}</div>
          )}
        </div>
      )}

      {/* Diary display */}
      {!isCourseShortNoticeCancelled && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="space-y-3">
            <h4 className="font-medium text-blue-900">Klassenbuch-Eintrag</h4>
            <p className="text-sm text-blue-800">
              {overviewData.lessonNote || (
                <span className="text-gray-500 italic">
                  Kein Eintrag vorhanden
                </span>
              )}
            </p>
            {diaryMeta && (overviewData?.lessonNote || "").trim() && (
              <div className="text-xs text-blue-700 mt-1">
                {(() => {
                  const d = new Date(diaryMeta.atISO);
                  const date = new Intl.DateTimeFormat("de-DE", {
                    timeZone: "Europe/Berlin",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  }).format(d);
                  const time = new Intl.DateTimeFormat("de-DE", {
                    timeZone: "Europe/Berlin",
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(d);
                  return `Geschrieben von ${diaryMeta.name} am ${date} um ${time} Uhr`;
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bänder log entry form (if this lesson has a band) */}
      {selectedLesson?.class_id && selectedLesson?.start_datetime && (
        <BandLogEntryForm
          lessonId={selectedLesson.id}
          classId={selectedLesson.class_id}
          dateStr={selectedLesson.start_datetime}
          // Bundle uses `period_number`; planner uses `block_number`. Accept
          // either so the form renders in both contexts.
          periodNumber={
            (selectedLesson as any).period_number ??
            (selectedLesson as any).block_number ??
            null
          }
        />
      )}

      {/* Concurrent course overlap boxes */}
      <ConcurrentCoursePanel
        courseOverlaps={courseOverlaps}
        concurrentCourseAttendance={concurrentCourseAttendance}
        transferredStudentIds={transferredStudentIds}
        lessonId={lessonId}
        isTransferring={isTransferring}
        pendingTransfer={pendingTransfer}
        setPendingTransfer={setPendingTransfer}
        transferReason={transferReason}
        setTransferReason={setTransferReason}
        onTransferConfirm={onTransferConfirm}
        variant="overview"
      />

      {/* Present students */}
      <div>
        <h4 className="flex items-center gap-2 mb-3">
          <Check className="h-5 w-5 text-green-600" /> Anwesend (
          {overviewData.present?.length || 0})
        </h4>
        <DebugOverlay
          id="ATT-OVERVIEW-PRESENT"
          name="AttendanceDialog.Overview.Present"
        >
          <div className="grid grid-cols-2 gap-2">
            {overviewData.present?.map((student: any) => (
              <div
                key={student.id}
                className="flex items-start gap-2 p-2 bg-green-50 rounded-lg overflow-hidden"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5"></div>
                <div className="min-w-0">
                  <span className="text-sm break-words">
                    {formatStudentName(student.name)}
                  </span>
                  {isCourse && student.className && (
                    <span className="text-xs text-gray-500 font-medium ml-1">
                      ({student.className})
                    </span>
                  )}
                  {/* Audit #23: Tagesgast indicator in the read-only
                      overview. Matches EditStudentRow's "Gast" pill so
                      both modes use the same visual cue. */}
                  {student.isGuest && (
                    <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">
                      Gast
                    </span>
                  )}
                  {student.notes && (
                    <div className="text-xs text-gray-500 italic truncate">
                      {student.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DebugOverlay>
      </div>

      {/* Late students */}
      {overviewData.late && overviewData.late.length > 0 && (
        <div>
          <h4 className="flex items-center gap-2 mb-3">
            <Clock className="h-5 w-5 text-yellow-600" /> Verspätet (
            {overviewData.late.length})
          </h4>
          <DebugOverlay
            id="ATT-OVERVIEW-LATE"
            name="AttendanceDialog.Overview.Late"
          >
            <div className="grid grid-cols-1 gap-2">
              {overviewData.late.map((student: any) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm">
                      {formatStudentName(student.name)}
                    </span>
                    {isCourse && student.className && (
                      <span className="text-xs text-gray-500 font-medium">
                        ({student.className})
                      </span>
                    )}
                    {/* Audit #23: Tagesgast pill (see Anwesend block). */}
                    {student.isGuest && (
                      <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">
                        Gast
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <Badge
                      variant="outline"
                      className="text-xs bg-yellow-100 text-yellow-800"
                    >
                      {student.minutesLate || 1} Min. verspätet
                    </Badge>
                    {student.arrivalTime && (
                      <div className="text-xs text-gray-500 mt-1">
                        Ankunft: {student.arrivalTime}
                      </div>
                    )}
                    {student.lateExcused && (
                      // Audit #19: same rationale as the absent badge below
                      // — align with the cross-feature "Informiert" naming.
                      <Badge
                        variant="outline"
                        className="text-xs mt-1 bg-blue-50 text-blue-700 border-blue-200"
                      >
                        Informiert
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DebugOverlay>
        </div>
      )}

      {/* Absent students */}
      {overviewData.absent && overviewData.absent.length > 0 && (
        <div>
          <h4 className="flex items-center gap-2 mb-3">
            <X className="h-5 w-5 text-red-500" /> Abwesend (
            {overviewData.absent.length})
          </h4>
          <DebugOverlay
            id="ATT-OVERVIEW-ABSENT"
            name="AttendanceDialog.Overview.Absent"
          >
            <div className="space-y-2">
              {overviewData.absent.map((student: any) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between p-2 bg-red-50 rounded-lg flex-wrap gap-1"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm">
                      {formatStudentName(student.name)}
                    </span>
                    {isCourse && student.className && (
                      <span className="text-xs text-gray-500 font-medium">
                        ({student.className})
                      </span>
                    )}
                    {/* Audit #23: Tagesgast pill (see Anwesend block). */}
                    {student.isGuest && (
                      <span className="ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">
                        Gast
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {student.excused ? (
                      // Audit #19: align lesson-attendance badge with the
                      // klassenuebersicht / dashboard convention from #11.
                      // absent_excused at the lesson level means "we have a
                      // note from a parent" — formal review/approval happens
                      // later in the Klärungsbedarf workflow. Render as the
                      // blue "Informiert" badge instead of secondary
                      // "Entschuldigt" so the wording matches everywhere
                      // the same status surfaces.
                      <Badge
                        variant="outline"
                        className="text-xs whitespace-normal text-left bg-blue-50 text-blue-700 border-blue-200"
                      >
                        Informiert
                        {student.reason && ` - ${student.reason}`}
                      </Badge>
                    ) : student.unclear ? (
                      // absent_unclear epic: pre-klärung absences get
                      // an orange "Ungeklärt" badge to distinguish from
                      // formal absent_unexcused (red "Unentschuldigt").
                      <Badge
                        variant="outline"
                        className="text-xs bg-orange-100 text-orange-800 border-orange-200"
                      >
                        Ungeklärt
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">
                        Unentschuldigt
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DebugOverlay>
        </div>
      )}
    </div>
  );
}
